import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { CostCentersService, CostCenterFilters } from '../../../services/cost-centers.service';
import { StatusService } from '../../../services/status.service';
import { 
  CostCenter, 
  ConsolidatedCost, 
  ConsolidatedBudget, 
  HierarchyLevel,
  ConsolidatedStatistics 
} from '../../../models/CostCenter';
import { Status } from '../../../models/Status';
import { TableAction } from '../../../shared/components/table/table.component';
import { ErrorService } from '../../../shared/services/error.service';
import { exportCostCentersToExcel } from '../../../utils/controladoria-export.util';

@Component({
  selector: 'app-cost-centers',
  templateUrl: './cost-centers.component.html',
  styleUrls: ['./cost-centers.component.css']
})
export class CostCentersComponent implements OnInit, OnDestroy {
  costCenters: CostCenter[] = [];
  isLoading = false;
  isExporting = false;

  // Filtros
  filters: CostCenterFilters & { status: string; searchTerm: string } = {
    status: '',
    searchTerm: '',
    page: 1,
    pageSize: 10
  };
  
  // Timeout para debounce da busca
  private searchTimeout: any;
  loadingStatuses = false;
  statusOptions: { value: string; label: string }[] = [];
  
  // Paginação
  pagination = {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1
  };

  // Configuração da tabela genérica
  columns = [
    { titulo: 'Código', label: 'code' },
    { titulo: 'Nome', label: 'name' },
    { titulo: 'Tipo', label: 'type' },
    { titulo: 'Departamento', label: 'department' },
    { titulo: 'Status', label: 'status' }
  ];

  // Ações da tabela
  actions: TableAction[] = [
    { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-view', visible: true },
    { label: 'Editar', icon: 'fas fa-pen-to-square', action: 'edit', class: 'btn-edit', visible: true },
    { label: 'Ver Consolidado', icon: 'fas fa-chart-bar', action: 'viewConsolidated', class: 'btn-info', visible: true },
    { label: 'Excluir', icon: 'fas fa-trash-can', action: 'delete', class: 'btn-delete', visible: true }
  ];

  // Dados consolidados para exibição
  showConsolidatedData = false;
  consolidatedData: {
    costCenter?: CostCenter;
    cost?: ConsolidatedCost;
    budget?: ConsolidatedBudget;
    level?: HierarchyLevel;
    descendants?: number;
  } | null = null;
  loadingConsolidated = false;

  /** Resumo exibido acima da grid (estatísticas consolidadas). */
  statisticsSummary: ConsolidatedStatistics | null = null;
  loadingStatisticsSummary = false;
  statisticsSummaryError: string | null = null;

  constructor(
    private router: Router,
    private costCentersService: CostCentersService,
    private statusService: StatusService,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.loadCostCenters();
    this.loadCostCenterStatuses();
    this.loadStatisticsSummary();
  }

  loadCostCenters(): void {
    this.isLoading = true;
    
    // Prepara os filtros removendo valores vazios
    const filters: CostCenterFilters = {
      page: this.filters.page || 1,
      pageSize: this.filters.pageSize || 10
    };
    
    if (this.filters.status?.trim()) filters.status = this.filters.status.trim();
    if (this.filters.searchTerm?.trim()) filters.searchTerm = this.filters.searchTerm.trim();
    
    this.costCentersService.getAll(filters).subscribe({
      next: res => { 
        this.costCenters = res.items || []; 
        this.pagination = {
          totalItems: res.totalItems || 0,
          totalPages: res.totalPages || 0,
          currentPage: res.page || 1
        };
        this.isLoading = false; 
      },
      error: _ => { 
        this.costCenters = [];
        this.isLoading = false; 
      }
    });
  }

  loadCostCenterStatuses(): void {
    this.loadingStatuses = true;
    this.statusService.getByEntity('CostCenter', true)
      .subscribe({
        next: (statuses: Status[]) => {
          this.statusOptions = [{ value: '', label: 'Todos os Status' }, ...statuses.map(s => ({ value: s.name, label: s.name }))];
          this.loadingStatuses = false;
        },
        error: (err) => {
          console.error('Erro ao carregar status de centros de custo:', err);
          this.loadingStatuses = false;
          // Fallback para status básicos em caso de erro
          this.statusOptions = [
            { value: '', label: 'Todos os Status' },
            { value: 'Ativo', label: 'Ativo' },
            { value: 'Encerrado', label: 'Encerrado' },
            { value: 'Inativo', label: 'Inativo' },
            { value: 'Suspenso', label: 'Suspenso' }
          ];
        }
      });
  }

  applyFilters(): void {
    // Reset para primeira página ao aplicar filtros
    this.filters.page = 1;
    this.loadCostCenters();
  }

  clearFilters(): void {
    this.filters = {
      status: '',
      searchTerm: '',
      page: 1,
      pageSize: 10
    };
    // Limpar também o campo de busca da tabela
    // Nota: A tabela será atualizada quando loadCostCenters() for chamado
    this.loadCostCenters();
  }
  
  ngOnDestroy(): void {
    // Limpar timeout ao destruir componente
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  onSearchChange(searchTerm: string): void {
    // Limpar timeout anterior se o usuário continuar digitando
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    
    // Atualiza o termo de busca nos filtros
    this.filters.searchTerm = searchTerm || '';
    
    // Aguardar 600ms após parar de digitar antes de fazer a requisição
    this.searchTimeout = setTimeout(() => {
      this.filters.page = 1; // Reset para primeira página
      this.loadCostCenters();
      this.searchTimeout = null;
    }, 600);
  }

  changePage(page: number): void {
    this.filters.page = page;
    this.loadCostCenters();
  }

  onPageChange(page: number): void {
    this.changePage(page);
  }

  /** Exporta todos os centros (filtros atuais), com visão completa dos campos da API. */
  exportToExcel(): void {
    if (this.isExporting) return;
    this.isExporting = true;

    const filters: CostCenterFilters = {
      page: 1,
      pageSize: Math.max(this.pagination.totalItems, 1) || 500
    };
    if (this.filters.status?.trim()) filters.status = this.filters.status.trim();
    if (this.filters.searchTerm?.trim()) filters.searchTerm = this.filters.searchTerm.trim();

    this.costCentersService.getAll(filters).subscribe({
      next: (res) => {
        const items = res.items || [];
        if (items.length === 0) {
          this.errorService.showErrorMessage('Não há centros de custo para exportar.');
        } else {
          exportCostCentersToExcel(items);
          this.errorService.showSuccessMessage(`Exportados ${items.length} centro(s) de custo.`);
        }
        this.isExporting = false;
      },
      error: () => {
        this.isExporting = false;
      }
    });
  }

  onActionClick(evt: { action: string, item: any }): void {
    const item = evt.item as CostCenter;
    if (!item) return;
    if (evt.action === 'view') {
      this.view(item);
    } else if (evt.action === 'edit') {
      this.edit(item);
    } else if (evt.action === 'viewConsolidated') {
      this.viewConsolidatedData(item);
    } else if (evt.action === 'delete') {
      if (item.id) this.remove(item.id);
    }
  }
  
  view(costCenter: CostCenter): void {
    const code = (costCenter.code || '').trim();
    if (!code) {
      return;
    }
    this.router.navigate(['/financial/cost-centers/code', code, 'view']);
  }

  edit(costCenter: CostCenter): void { 
    this.router.navigate(['/financial/cost-centers', costCenter.id, 'edit']); 
  }

  newCostCenter(): void { 
    this.router.navigate(['/financial/cost-centers/new']); 
  }

  remove(id: number): void { 
    if (confirm('Tem certeza que deseja excluir este centro de custo?')) {
      this.costCentersService.delete(id).subscribe(() => this.loadCostCenters());
    }
  }

  // ========== NOVOS MÉTODOS - Endpoints Avançados ==========

  /**
   * Carrega e exibe dados consolidados de um centro de custo
   */
  viewConsolidatedData(costCenter: CostCenter): void {
    if (!costCenter.id) return;

    this.loadingConsolidated = true;
    this.showConsolidatedData = true;
    this.consolidatedData = { costCenter };

    // Carrega custo consolidado, orçamento consolidado, nível hierárquico e descendentes em paralelo
    Promise.all([
      this.costCentersService.getConsolidatedCost(costCenter.id).toPromise(),
      this.costCentersService.getConsolidatedBudget(costCenter.id).toPromise(),
      this.costCentersService.getHierarchyLevel(costCenter.id).toPromise(),
      this.costCentersService.getAllDescendants(costCenter.id).toPromise()
    ])
      .then(([cost, budget, level, descendants]) => {
        this.consolidatedData = {
          costCenter,
          cost,
          budget,
          level,
          descendants: descendants?.totalDescendants || 0
        };
        this.loadingConsolidated = false;
      })
      .catch(err => {
        console.error('Erro ao carregar dados consolidados:', err);
        this.loadingConsolidated = false;
      });
  }

  /**
   * Fecha o modal de dados consolidados
   */
  closeConsolidatedData(): void {
    this.showConsolidatedData = false;
    this.consolidatedData = null;
  }

  /** Carrega resumo consolidado para exibir acima da grid. */
  loadStatisticsSummary(): void {
    this.loadingStatisticsSummary = true;
    this.statisticsSummaryError = null;

    this.costCentersService.getActive({ page: 1, pageSize: 500 }).subscribe({
      next: (result) => {
        const items = result.items || [];
        const rootIds = this.costCentersService.resolveRootCostCenterIds(items);

        if (rootIds.length === 0) {
          this.loadingStatisticsSummary = false;
          this.statisticsSummary = {
            totalCostCenters: 0,
            statistics: [],
            summary: { totalBudget: 0, totalCost: 0, overBudgetCount: 0 }
          };
          return;
        }

        this.costCentersService
          .getConsolidatedStatistics(rootIds, { suppressErrorToast: true })
          .subscribe({
            next: (stats) => {
              this.statisticsSummary = stats;
              this.loadingStatisticsSummary = false;
            },
            error: () => {
              this.loadingStatisticsSummary = false;
              this.statisticsSummaryError = 'Não foi possível carregar o resumo consolidado.';
            }
          });
      },
      error: () => {
        this.loadingStatisticsSummary = false;
        this.statisticsSummaryError = 'Não foi possível carregar o resumo consolidado.';
      }
    });
  }

  openConsolidatedStatisticsReport(): void {
    this.router.navigate(['/financial/cost-centers/consolidated-statistics']);
  }

  /**
   * Calcula a variação percentual entre custo e orçamento
   */
  calculateVariance(cost?: number, budget?: number): number {
    if (!cost || !budget || budget === 0) return 0;
    return ((cost - budget) / budget) * 100;
  }
}
